import React, { useEffect, useRef, useState } from "react";
import RowResultsTable from "./RowResultsTable";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function secondsAgo(iso) {
  if (!iso) return null;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
}

export default function ActiveRunPanel({ job, onCancel, onRetry, onPublish }) {
  const [tableExpanded, setTableExpanded] = useState(true);
  const [cancelClicked, setCancelClicked] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [publishClicked, setPublishClicked] = useState(false);
  const liveLogRef = useRef(null);
  const liveLog = (job && job.status === "running" && job.live_log) || [];

  // Keep the live log pinned to the newest lines as they stream in.
  useEffect(() => {
    if (liveLogRef.current) {
      liveLogRef.current.scrollTop = liveLogRef.current.scrollHeight;
    }
  }, [liveLog.join("\n")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!job) return null;
  const p = job.progress || {};
  const total = p.total_rows || 0;
  const done = p.processed_rows || 0;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const cancelling = cancelClicked || Boolean(job.cancel_requested);

  const handleCancelClick = async () => {
    if (!window.confirm(
      "Cancel this run? It stops at the next safe point (usually within seconds). Already-uploaded PDFs stay in Drive."
    )) return;
    setCancelClicked(true);
    if (onCancel) await onCancel();
  };

  // Anything that isn't a success or a deliberate skip is worth another go:
  // failed rows, and rows left pending/running when the run aborted.
  const rows = job.rows || [];
  const unfinished = rows.filter((r) => ["failed", "pending", "running"].includes(r.status)).length;
  const canRetry = job.status !== "running" && onRetry && unfinished > 0;

  const handleRetryClick = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } catch (_) {
      // index.js already put the error in the banner.
    } finally {
      setRetrying(false);
    }
  };

  // Publishing to the live Dashboards folder: every succeeded report that
  // isn't already there. Pressing again retries the failed ones only.
  const pub = job.publish || null;
  const publishing = publishClicked || Boolean(pub && pub.status === "running");
  const publishable = rows.filter(
    (r) => r.status === "succeeded" && !(r.publish && r.publish.status === "published")
  );
  const canPublish = job.status !== "running" && onPublish && !publishing && publishable.length > 0;
  const onlyRetries = publishable.every((r) => r.publish && r.publish.status === "failed");
  const reportWord = publishable.length === 1 ? "report" : "reports";

  const handlePublishClick = async () => {
    if (!window.confirm(
      `Copy ${publishable.length} ${reportWord} into the live Dashboards folder? ` +
      "Each goes into its franchisee's folder; a same-name PDF already there is replaced."
    )) return;
    setPublishClicked(true);
    try {
      await onPublish();
    } catch (_) {
      // index.js already put the error in the banner.
    } finally {
      setPublishClicked(false);
    }
  };

  const heading = {
    running: "Run in progress",
    cancelled: "Run cancelled",
    failed: "Run failed",
  }[job.status] || "Run finished";

  const heartbeatAge = job.status === "running" ? secondsAgo(job.heartbeat_at) : null;

  return (
    <div className="bfr-active-panel">
      <div className="bfr-active-panel-header">
        Started {formatDate(job.started_at)} by {job.started_by_user || "—"} ·
        Period: {job.period && job.period.month_name} {job.period && job.period.year}
        {job.status === "running" && onCancel && (
          <button
            className="bfr-cancel-button"
            onClick={handleCancelClick}
            disabled={cancelling}
          >
            {cancelling ? "Cancelling…" : "Cancel run"}
          </button>
        )}
      </div>
      <h2>{heading}</h2>
      <div className="bfr-progressbar-wrap">
        <div className="bfr-progressbar">
          <div className="bfr-progressbar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="bfr-progressbar-text">
          {done} of {total} rows ({pct}%)
        </div>
      </div>
      <div className="bfr-stage">{job.current_stage || ""}</div>
      {heartbeatAge !== null && (
        <div className={`bfr-heartbeat${heartbeatAge > 120 ? " stale" : ""}`}>
          Last backend activity: {heartbeatAge}s ago
          {heartbeatAge > 120 && " — the run may have died; it will be marked failed automatically."}
        </div>
      )}
      {job.error && <div className="bfr-error-banner">{job.error}</div>}
      {canRetry && (
        <div className="bfr-retry-wrap">
          <button className="bfr-run-button" onClick={handleRetryClick} disabled={retrying}>
            {retrying ? "Starting…" : `Retry ${unfinished} failed / unfinished`}
          </button>
          <span className="bfr-retry-hint">
            Succeeded stores are kept; only the rest are exported again. Press as often as needed.
          </span>
        </div>
      )}
      <div className="bfr-counts">
        <span className="ok">✓ Succeeded: {p.succeeded || 0}</span>
        <span className="skip">⊘ Skipped: {p.skipped || 0}</span>
        <span className="fail">✗ Failed: {p.failed || 0}</span>
      </div>

      {canPublish && (
        <div className="bfr-retry-wrap">
          <button className="bfr-run-button" onClick={handlePublishClick} disabled={publishing}>
            {onlyRetries
              ? `Retry publishing ${publishable.length} ${reportWord}`
              : `Publish ${publishable.length} ${reportWord} to live Dashboards`}
          </button>
          <span className="bfr-retry-hint">
            Copies each PDF into that franchisee's folder in the live Dashboards tree.
            Already-published reports are skipped.
          </span>
        </div>
      )}
      {pub && (
        <div className={`bfr-publish-status ${pub.status}`}>
          {{
            running: "Publishing to live Dashboards…",
            completed: "Published to live Dashboards",
            failed: "Publishing failed",
          }[pub.status] || pub.status}
          {" · "}{pub.published || 0} published · {pub.failed || 0} failed
          {pub.started_by_user && ` · by ${pub.started_by_user}`}
        </div>
      )}
      {pub && pub.error && <div className="bfr-error-banner">{pub.error}</div>}

      {liveLog.length > 0 && (
        <div className="bfr-live-log-wrap">
          <h3>Live activity</h3>
          <pre className="bfr-live-log" ref={liveLogRef}>{liveLog.join("\n")}</pre>
        </div>
      )}

      <div className="bfr-row-table-wrap">
        <button
          className="bfr-row-table-toggle"
          onClick={() => setTableExpanded((v) => !v)}
        >
          {tableExpanded ? "Hide per-row results" : "Show per-row results"}
        </button>
        {tableExpanded && <RowResultsTable rows={job.rows || []} />}
      </div>
    </div>
  );
}
